import React, { useEffect, useState, useRef } from 'react';
import { Bell, TrendingUp, Calendar, FileText, X, Sparkles, Plus, Edit, Trash2 } from 'lucide-react';
import { GridItem, useColorMode, useColorModeValue } from '@chakra-ui/react';
import MultiSelectDropdown from 'components/ui/MultiSelectDropdown';
import { useDispatch, useSelector } from 'react-redux';
import { renderDynamicDropdown } from 'components/ui/DynamicDropdown';
import { useUser } from 'contexts';
import { getItems } from '../../redux/actions/item';
import { useGlobal } from 'contexts';
import {
   addNotifyMe,
   getNotifyMe,
   updateNotifyMe,
   deleteNotifyMe,
   updateNotifyMeStatus,
} from '../../redux/actions/notifyMe';
import ItemMasterForm from 'views/admin/masters/Pages/ItemMaster/ItemMasterForm';

const NotifyMe = ({ onClose }) => {
   const [showForm, setShowForm] = useState(false);
   const [editingAlert, setEditingAlert] = useState(null);
   const [formData, setFormData] = useState({
      accountId: [],
      itemIds: [],
      fromRate: '',
      toRate: '',
      notes: '',
      noOfDays: '',
   });
   const [errors, setErrors] = useState({});
   const { user } = useUser();
   const dispatch = useDispatch();
   const { userGlobalSettingsData } = useGlobal();
   const textColor = useColorModeValue('#9E9E9E', 'white');
   const isColorMode = useColorModeValue(true, false);
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const [submitted, setSubmitted] = useState(false);
   const [deletingId, setDeletingId] = useState(null);
   const [updatingStatusId, setUpdatingStatusId] = useState(null);
   const accountData = useSelector((state) => state.account.list || []);
   const itemData = useSelector((state) => state.item.data || []);
   const globalSettingItem = userGlobalSettingsData?.[0]?.askCompany?.item;
   const shouldShowCompanyDropdownItem = globalSettingItem || false;

   // Refs for focus management
   const accountDropdownRef = useRef(null);
   const itemDropdownRef = useRef(null);
   const fromRateRef = useRef(null);
   const toRateRef = useRef(null);
   const noOfDaysRef = useRef(null);
   const notesRef = useRef(null);
   const submitButtonRef = useRef(null);
   const backButtonRef = useRef(null);

   const notifyMeData = useSelector((state) => state?.NotifyMe?.list) || [];
   useEffect(() => {
      dispatch(getNotifyMe({ companyId: user?.loginhistory?.companyId }));
   }, [dispatch, user?.loginhistory?.companyId]);
   useEffect(() => {
      dispatch(
         getItems({
            companyId: user?.loginhistory?.companyId,
            ...(shouldShowCompanyDropdownItem && {
               companyIds: user?.loginhistory?.companyId,
            }),
            status: true,
         }),
      );
   }, [dispatch, user?.loginhistory?.companyId, shouldShowCompanyDropdownItem]);

   const formatDate = (dateString) => {
      if (!dateString) return 'N/A';

      try {
         const date = new Date(dateString);
         if (isNaN(date.getTime())) return 'N/A';

         return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
         });
      } catch (error) {
         console.error('Error formatting date:', error);
         return 'N/A';
      }
   };
   // ESC key handler
   useEffect(() => {
      const handleEscKey = (e) => {
         const notify = localStorage.getItem('notifyme');
         console.log('key keybord', e.key);
         console.log('notify key keybord', notify);
         if (e.key === 'Escape') {
            e.stopPropagation();
            if (showForm) {
               // If form is open, go back to list
               handleBackToList();
            } else {
               // If list is showing, close the modal
               onClose();
            }
         }
      };

      document.addEventListener('keydown', handleEscKey, true);
      return () => {
         document.removeEventListener('keydown', handleEscKey, true);
      };
   }, [showForm, onClose]);

   useEffect(() => {
      const handleEscAction = () => {
         if (showForm) {
            handleBackToList();
         } else {
            onClose();
         }
      };

      window.addEventListener('GLOBAL_ESC', handleEscAction);
      return () => window.removeEventListener('GLOBAL_ESC', handleEscAction);
   }, [showForm, onClose]);

   // Focus management when form opens
   useEffect(() => {
      if (showForm && accountDropdownRef.current) {
         // Small timeout to ensure the DOM is updated
         setTimeout(() => {
            focusAccountDropdown();
         }, 100);
      }
   }, [showForm]);

   // Function to focus account dropdown
   const focusAccountDropdown = () => {
      if (accountDropdownRef.current) {
         const inputElement = accountDropdownRef.current.querySelector('input');
         if (inputElement) {
            inputElement.focus();
         }
      }
   };
   const getFocusableElements = () => {
      const elements = [];

      // Account dropdown - find the actual input inside
      if (accountDropdownRef.current) {
         const accountInput = accountDropdownRef.current.querySelector(
            'input[type="text"], input:not([type="hidden"])',
         );
         if (accountInput) elements.push(accountInput);
      }

      // Item dropdown - find input in MultiSelectDropdown
      if (itemDropdownRef.current) {
         // Try multiple possible selectors for the input in MultiSelectDropdown
         const itemInput = itemDropdownRef.current.querySelector(
            'input[type="text"], input[type="search"], input:not([type="hidden"])',
         );

         // If no direct input found, look for any focusable element
         if (!itemInput) {
            const focusableItem = itemDropdownRef.current.querySelector('button, [tabindex]:not([tabindex="-1"])');
            if (focusableItem) elements.push(focusableItem);
         } else {
            elements.push(itemInput);
         }
      }

      // Other form fields
      if (fromRateRef.current) elements.push(fromRateRef.current);
      if (toRateRef.current) elements.push(toRateRef.current);
      if (noOfDaysRef.current) elements.push(noOfDaysRef.current);
      if (notesRef.current) elements.push(notesRef.current);
      if (submitButtonRef.current) elements.push(submitButtonRef.current);
      if (backButtonRef.current) elements.push(backButtonRef.current);

      // Filter out hidden/disabled elements and ensure they're focusable
      return elements.filter(
         (el) => el && el.offsetParent !== null && !el.disabled && (el.tabIndex === undefined || el.tabIndex >= 0),
      );
   };

   // Helper function to get current focus index
   const getCurrentFocusIndex = (focusableElements) => {
      const activeElement = document.activeElement;

      // Direct match
      let index = focusableElements.indexOf(activeElement);
      if (index !== -1) return index;

      // Check if active element is inside account dropdown
      if (accountDropdownRef.current && accountDropdownRef.current.contains(activeElement)) {
         const accountInput = focusableElements.find((el) => accountDropdownRef.current.contains(el));
         if (accountInput) return focusableElements.indexOf(accountInput);
      }

      // Check if active element is inside item dropdown
      if (itemDropdownRef.current && itemDropdownRef.current.contains(activeElement)) {
         const itemInput = focusableElements.find((el) => itemDropdownRef.current.contains(el));
         if (itemInput) return focusableElements.indexOf(itemInput);
      }

      return -1;
   };

   // Improved focus management for the item dropdown
   const focusItemDropdown = () => {
      if (itemDropdownRef.current) {
         // Try to find and focus the input element
         const itemInput = itemDropdownRef.current.querySelector(
            'input[type="text"], input[type="search"], input:not([type="hidden"])',
         );

         if (itemInput) {
            itemInput.focus();
         } else {
            // If no input found, try to focus the first focusable element
            const focusableElement = itemDropdownRef.current.querySelector('button, [tabindex]:not([tabindex="-1"])');
            if (focusableElement) {
               focusableElement.focus();
            }
         }
      }
   };

   // Add this useEffect to your Cash and Bank modal components
   useEffect(() => {
      const handleEscKey = (e) => {
         if (e.key === 'Escape') {
            onClose(); // or whatever function closes your modal
         }
      };

      document.addEventListener('keydown', handleEscKey);

      return () => {
         document.removeEventListener('keydown', handleEscKey);
      };
   }, [onClose]);

   // Update the Enter key handler to include specific logic for account dropdown
   const handleKeyDown = (e) => {
      // Handle Enter key - move to next field instead of submitting
      if (e.key === 'Enter') {
         e.preventDefault();
         e.stopPropagation();

         const focusableElements = getFocusableElements();
         const currentIndex = getCurrentFocusIndex(focusableElements);

         // Special case: if we're in account dropdown and press Enter, go to item dropdown
         if (accountDropdownRef.current && accountDropdownRef.current.contains(document.activeElement)) {
            focusItemDropdown();
            return;
         }

         const nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;

         if (focusableElements[nextIndex]) {
            focusableElements[nextIndex].focus();
         }
         return;
      }

      // Handle Tab key navigation
      if (e.key === 'Tab') {
         e.preventDefault();
         e.stopPropagation();

         const focusableElements = getFocusableElements();
         const currentIndex = getCurrentFocusIndex(focusableElements);
         let nextIndex;

         if (e.shiftKey) {
            // Shift + Tab - move backward
            nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
         } else {
            // Tab - move forward
            nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
         }

         if (focusableElements[nextIndex]) {
            focusableElements[nextIndex].focus();
         }
      }
   };

   // Validation function
   const validateForm = () => {
      const newErrors = {};

      // Account is required
      if (!formData.accountId || formData.accountId.length === 0) {
         newErrors.accountId = 'Account is required';
      }

      // Number of days is required
      if (!formData.noOfDays || formData.noOfDays <= 0) {
         newErrors.noOfDays = 'Number of days is required and must be greater than 0';
      }

      // Either items OR rate range must be provided
      const hasItems = formData.itemIds && formData.itemIds.length > 0;
      const hasFromRate = formData.fromRate && formData.fromRate !== '';
      const hasToRate = formData.toRate && formData.toRate !== '';
      const hasRateRange = hasFromRate || hasToRate;

      if (!hasItems && !hasRateRange) {
         newErrors.itemOrRate = 'Either select items OR provide rate range';
      }

      // Rate range validation - if any rate field has value, both are required
      if (hasFromRate && !hasToRate) {
         newErrors.rateRange = 'Maximum rate is required when minimum rate is provided';
      } else if (!hasFromRate && hasToRate) {
         newErrors.rateRange = 'Minimum rate is required when maximum rate is provided';
      } else if (hasFromRate && hasToRate) {
         // If both are provided, validate that fromRate < toRate
         const fromRateNum = parseFloat(formData.fromRate);
         const toRateNum = parseFloat(formData.toRate);

         if (fromRateNum >= toRateNum) {
            newErrors.rateRange = 'Minimum rate should be less than maximum rate';
         }
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
   };

   const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData({
         ...formData,
         [name]: value,
      });

      // Clear error for this field when user starts typing
      if (errors[name]) {
         setErrors({
            ...errors,
            [name]: '',
         });
      }

      // Clear rate range error when either fromRate or toRate changes
      if ((name === 'fromRate' || name === 'toRate') && errors.rateRange) {
         setErrors({
            ...errors,
            rateRange: '',
         });
      }
   };

   const handleAccountChange = (selectedValues) => {
      setFormData((prev) => ({ ...prev, accountId: selectedValues || [] }));

      // Clear account error
      if (errors.accountId) {
         setErrors({
            ...errors,
            accountId: '',
         });
      }
   };

   const handleItemChange = (selectedValues) => {
      setFormData((prev) => ({ ...prev, itemIds: selectedValues || [] }));

      // Clear item/rate error when items are selected
      if (errors.itemOrRate) {
         setErrors({
            ...errors,
            itemOrRate: '',
         });
      }
   };

   const handleSubmit = async (e) => {
      e.preventDefault();

      // Validate form
      if (!validateForm()) {
         return;
      }

      setSubmitted(true);

      const alertData = {
         companyId: user?.loginhistory?.companyId,
         accountId: formData.accountId,
         itemIds: formData.itemIds,
         fromRate: formData.fromRate ? parseFloat(formData.fromRate) : null,
         toRate: formData.toRate ? parseFloat(formData.toRate) : null,
         notes: formData.notes,
         noOfDays: formData.noOfDays ? parseInt(formData.noOfDays) : null,
      };

      // Add ID if editing
      if (editingAlert) {
         alertData.id = editingAlert.id;
      }

      console.log('📊 Rate Alert Form Data Submitted:', alertData);

      try {
         const result = editingAlert
            ? await dispatch(updateNotifyMe(alertData))
            : await dispatch(addNotifyMe(alertData));

         console.log('API Result:', result);

         // SUCCESS - close form and reset
         // Reset form data
         setFormData({
            accountId: [],
            itemIds: [],
            fromRate: '',
            toRate: '',
            notes: '',
            noOfDays: '',
         });

         // Clear errors
         setErrors({});

         // Reset submitted state
         setSubmitted(false);

         // Clear editing state
         setEditingAlert(null);

         // Close the form and show table - ONLY ON SUCCESS
         setShowForm(false);

         // Refresh the data
         dispatch(getNotifyMe({ companyId: user?.loginhistory?.companyId }));

         console.log(`✅ Alert ${editingAlert ? 'updated' : 'created'} - Form closed and data refreshed`);
      } catch (error) {
         console.error(`❌ Error ${editingAlert ? 'updating' : 'creating'} alert:`, error);

         // ERROR - don't close the form, just reset submitted state
         setSubmitted(false);

         // Form remains open for user to correct the errors
         console.log('Form remains open due to error');
      }
   };

   const handleAddNew = () => {
      setEditingAlert(null);
      resetForm();
      setShowForm(true);
   };

   const handleEdit = (alert) => {
      setEditingAlert(alert);
      setFormData({
         accountId: alert.accountId || [],
         itemIds: alert.itemIds || [],
         fromRate: alert.fromRate || '',
         toRate: alert.toRate || '',
         notes: alert.notes || '',
         noOfDays: alert.noOfDays || '',
      });
      setErrors({});
      setShowForm(true);
   };

   const handleDelete = async (id) => {
      if (window.confirm('Are you sure you want to delete this alert?')) {
         setDeletingId(id);
         try {
            const result = await dispatch(deleteNotifyMe(id));
            if (result && result.success) {
               console.log('✅ Alert deleted successfully');
               // Refresh the notifications list
               dispatch(getNotifyMe({ companyId: user?.loginhistory?.companyId }));
            } else {
               console.error('❌ Failed to delete alert:', result);
            }
         } catch (error) {
            console.error('❌ Error deleting alert:', error);
         } finally {
            setDeletingId(null);
         }
      }
   };

   const handleStatusToggle = async (alert) => {
      setUpdatingStatusId(alert.id);
      try {
         const newStatus = !alert.status;
         const statusData = {
            id: Number(alert.id),
            status: newStatus,
         };

         console.log('🔄 Updating status:', statusData);

         const result = await dispatch(updateNotifyMeStatus(statusData));
         if (result && result.success) {
            console.log(`✅ Alert status updated to ${newStatus}`);
            // Refresh the notifications list
            dispatch(getNotifyMe({ companyId: user?.loginhistory?.companyId }));
         } else {
            console.error('❌ Failed to update alert status:', result);
         }
      } catch (error) {
         console.error('❌ Error updating alert status:', error);
      } finally {
         setUpdatingStatusId(null);
      }
   };

   const resetForm = () => {
      setFormData({
         accountId: [],
         itemIds: [],
         fromRate: '',
         toRate: '',
         notes: '',
         noOfDays: '',
      });
      setErrors({});
      setSubmitted(false);
   };

   const resetFormAndClose = () => {
      setFormData({
         accountId: [],
         itemIds: [],
         fromRate: '',
         toRate: '',
         notes: '',
         noOfDays: '',
      });
      setErrors({});
      setSubmitted(false);
      setEditingAlert(null);
      setShowForm(false);
   };

   // Also update the handleBackToList function for consistency
   const handleBackToList = () => {
      resetFormAndClose();
   };

   const getStatusBadge = (alert) => {
      const isUpdating = updatingStatusId === alert.id;

      return (
         <button
            onClick={() => handleStatusToggle(alert)}
            disabled={isUpdating}
            className={`px-3 py-1 text-xs rounded-full font-semibold transition-all transform hover:scale-105 ${
               isUpdating
                  ? 'bg-gray-500/20 text-gray-500 cursor-not-allowed'
                  : alert.status
                  ? 'bg-green-500/20 text-green-500 hover:bg-green-500/30 cursor-pointer'
                  : 'bg-red-500/20 text-red-500 hover:bg-red-500/30 cursor-pointer'
            }`}>
            {isUpdating ? (
               <div className="flex items-center gap-1">
                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                     <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                     />
                  </svg>
                  Updating...
               </div>
            ) : alert.status ? (
               'Active'
            ) : (
               'Inactive'
            )}
         </button>
      );
   };

   // Render Table View
   const renderTableView = () => (
      <div className="space-y-6 pb-6">
         {/* Table */}
         {notifyMeData.length > 0 ? (
            <div
               className={`rounded-2xl overflow-hidden border ${
                  isDark ? 'border-violet-500/20 bg-slate-900/50' : 'border-violet-200 bg-white'
               }`}>
               {/* Add max-height and overflow-y-auto for vertical scroll */}
               <div
                  className={`overflow-x-auto overflow-y-auto max-h-[500px] ${
                     isDark ? 'scrollbar-dark' : 'scrollbar-light'
                  }`}
                  style={{
                     scrollbarWidth: 'thin',
                     scrollbarColor: isDark ? '#6366f1 #1e293b' : '#8b5cf6 #f3f4f6',
                  }}>
                  <table className="w-full">
                     <thead
                        className={`sticky rounded-t-2xl top-0 z-10 ${
                           isDark ? 'bg-slate-800/95' : 'bg-violet-50/95'
                        } backdrop-blur-sm `}>
                        <tr>
                           <th
                              className={`px-6 py-4 text-left text-sm font-semibold ${
                                 isDark ? 'text-gray-300' : 'text-slate-700'
                              } first:rounded-tl-2xl`}>
                              Account
                           </th>
                           <th
                              className={`px-6 py-4 text-left text-sm font-semibold ${
                                 isDark ? 'text-gray-300' : 'text-slate-700'
                              }`}>
                              Items
                           </th>
                           <th
                              className={`px-6 py-4 text-left text-sm font-semibold ${
                                 isDark ? 'text-gray-300' : 'text-slate-700'
                              }`}>
                              Rate Range
                           </th>
                           <th
                              className={`px-6 py-4 text-left text-sm font-semibold ${
                                 isDark ? 'text-gray-300' : 'text-slate-700'
                              }`}>
                              Days
                           </th>
                           <th
                              className={`px-6 py-4 text-left text-sm font-semibold ${
                                 isDark ? 'text-gray-300' : 'text-slate-700'
                              }`}>
                              Create By
                           </th>
                           <th
                              className={`px-6 py-4 text-left text-sm font-semibold ${
                                 isDark ? 'text-gray-300' : 'text-slate-700'
                              }`}>
                              Create At
                           </th>
                           <th
                              className={`px-6 py-4 text-left text-sm font-semibold ${
                                 isDark ? 'text-gray-300' : 'text-slate-700'
                              }`}>
                              Notes
                           </th>
                           <th
                              className={`px-6 py-4 text-left text-sm font-semibold ${
                                 isDark ? 'text-gray-300' : 'text-slate-700'
                              }`}>
                              Status
                           </th>
                           <th
                              className={`px-6 py-4 text-left text-sm font-semibold ${
                                 isDark ? 'text-gray-300' : 'text-slate-700'
                              }`}>
                              Actions
                           </th>
                        </tr>
                     </thead>
                     <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {notifyMeData.map((alert, index) => (
                           <tr
                              key={alert.id}
                              className={`transition-all hover:${isDark ? 'bg-slate-800/30' : 'bg-violet-50/30'}`}>
                              <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                                 {alert.accountName}
                              </td>
                              <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                                 {alert.items?.map((item) => item.name).join(', ')}
                              </td>
                              <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                                 {alert.fromRate || 0} - {alert.toRate || '∞'}
                              </td>
                              <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                                 {alert.noOfDays || 'N/A'}
                              </td>
                              <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                                 {alert.createdBy || 'N/A'}
                              </td>
                              <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                                 {formatDate(alert.createdAt)}
                              </td>
                              <td
                                 className={`px-6 py-4 text-sm ${
                                    isDark ? 'text-gray-400' : 'text-slate-600'
                                 } max-w-xs truncate`}
                                 title={alert.notes}>
                                 {alert.notes || '-'}
                              </td>
                              <td className="px-6 py-4">{getStatusBadge(alert)}</td>
                              <td className="px-6 py-4">
                                 <div className="flex items-center gap-2">
                                    <button
                                       onClick={() => handleEdit(alert)}
                                       className={`p-2 rounded-lg transition-all ${
                                          isDark
                                             ? 'bg-slate-700 hover:bg-slate-600 text-gray-300'
                                             : 'bg-violet-100 hover:bg-violet-200 text-violet-600'
                                       }`}>
                                       <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                       onClick={() => handleDelete(alert.id)}
                                       disabled={deletingId === alert.id}
                                       className={`p-2 rounded-lg transition-all ${
                                          isDark
                                             ? 'bg-slate-700 hover:bg-slate-600 text-gray-300'
                                             : 'bg-red-100 hover:bg-red-200 text-red-600'
                                       } disabled:opacity-50 disabled:cursor-not-allowed`}>
                                       {deletingId === alert.id ? (
                                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                             <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                                fill="none"
                                             />
                                          </svg>
                                       ) : (
                                          <Trash2 className="w-4 h-4" />
                                       )}
                                    </button>
                                 </div>
                              </td>
                           </tr>
                        ))}
                     </tbody>
                  </table>
               </div>
            </div>
         ) : (
            <div
               className={`text-center py-12 rounded-2xl border-2 border-dashed ${
                  isDark ? 'border-violet-500/20 bg-slate-900/30' : 'border-violet-200 bg-violet-50/50'
               }`}>
               <Bell className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-violet-500/40' : 'text-violet-400'}`} />
               <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  No alerts found
               </h3>
               <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                  Create your first rate alert to get started
               </p>
               <button
                  onClick={handleAddNew}
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                     isDark
                        ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white'
                        : 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white'
                  } shadow-lg hover:shadow-xl`}>
                  <Plus className="w-5 h-5" />
                  Create First Alert
               </button>
            </div>
         )}
      </div>
   );

   // Render Form View
   const renderFormView = () => (
      <div
         className={`overflow-y-auto overflow-x-hidden width-full max-h-[600px] ${
            isDark ? 'scrollbar-dark' : 'scrollbar-light'
         }`}
         style={{
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? '#6366f1 #1e293b' : '#8b5cf6 #f3f4f6',
         }}>
         <form
            onSubmit={handleSubmit}
            onKeyDown={handleKeyDown}
            onKeyPress={(e) => {
               // Prevent form submission on Enter key press
               if (e.key === 'Enter') {
                  e.preventDefault();
               }
            }}
            className="space-y-3 px-1">
            {/* General validation error */}
            {errors.itemOrRate && (
               <div
                  className={`p-4 rounded-xl ${
                     isDark ? 'bg-red-500/20 border border-red-500/30' : 'bg-red-50 border border-red-200'
                  }`}>
                  <p className={`text-sm font-semibold ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                     {errors.itemOrRate}
                  </p>
               </div>
            )}

            {/* Account & Item */}
            <div className="grid grid-cols-1 mt-3 md:grid-cols-2 gap-6">
               <div className="space-y-2">
                  <GridItem>
                     <div ref={accountDropdownRef}>
                        {renderDynamicDropdown(
                           'accountId',
                           'Account',
                           accountData,
                           {
                              value: formData.accountId,
                              onChange: handleAccountChange,
                              showCustomNoOptions: true,
                              openfrom: 'account',
                           },
                           {},
                           textColor,
                           true,
                           'Select Account',
                           true,
                           undefined,
                           false,
                        )}
                     </div>
                  </GridItem>
                  {errors.accountId && (
                     <p className={`text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>{errors.accountId}</p>
                  )}
               </div>

               <div ref={itemDropdownRef} className="focusable-dropdown-wrapper" tabIndex={-1}>
                  <MultiSelectDropdown
                     label="Item"
                     options={itemData}
                     value={formData.itemIds}
                     onChange={handleItemChange}
                     placeholder="Select Items"
                     isRequired={false}
                     showAddButton={true}
                     onAddNew={(value) => console.log('handleAddNew', value)}
                     isLoading={false}
                     darkMode={!isColorMode}
                     ItemFormComponent={ItemMasterForm}
                     error={''}
                     autoFocus={false}
                     enableTabNavigation={true}
                  />
               </div>
            </div>

            {/* Rate Range */}
            <div className="space-y-2">
               <label
                  className={`block text-sm font-semibold ${
                     isDark ? 'text-gray-300' : 'text-slate-700'
                  } mb-2 flex items-center gap-2`}>
                  <TrendingUp className={`w-4 h-4 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
                  Rate Range
               </label>
               <div className="relative">
                  <div
                     className={`absolute inset-0 bg-gradient-to-r ${
                        isDark ? 'from-violet-600/10 to-fuchsia-600/10' : 'from-violet-100 to-fuchsia-100'
                     } rounded-xl blur`}
                  />
                  <div
                     className={`relative ${
                        isDark
                           ? 'bg-slate-800/30 border-violet-500/30'
                           : 'bg-gradient-to-br from-violet-50 to-fuchsia-50 border-violet-300'
                     } border rounded-xl p-4`}>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="relative">
                           <input
                              ref={fromRateRef}
                              type="number"
                              name="fromRate"
                              value={formData.fromRate}
                              onChange={handleChange}
                              onWheel={(e) => e.target.blur()}
                              step="0.01"
                              className={`w-full px-4 py-3 ${
                                 isDark
                                    ? 'bg-slate-900/50 border-violet-500/20 text-white placeholder-gray-500'
                                    : 'bg-white border-violet-200 text-slate-900 placeholder-slate-400'
                              } border rounded-lg focus:border-violet-500 focus:ring-2 focus:ring-violet-500/50 transition-all outline-none`}
                              placeholder="0.00"
                           />
                           <span
                              className={`absolute right-4 top-1/2 transform -translate-y-1/2 text-xs ${
                                 isDark ? 'text-gray-400 bg-slate-950/80' : 'text-violet-600 bg-violet-100'
                              } px-2 py-1 rounded font-semibold`}>
                              MIN
                           </span>
                        </div>
                        <div className="relative">
                           <input
                              ref={toRateRef}
                              type="number"
                              name="toRate"
                              value={formData.toRate}
                              onChange={handleChange}
                              onWheel={(e) => e.target.blur()}
                              step="0.01"
                              className={`w-full px-4 py-3 ${
                                 isDark
                                    ? 'bg-slate-900/50 border-violet-500/20 text-white placeholder-gray-500'
                                    : 'bg-white border-violet-200 text-slate-900 placeholder-slate-400'
                              } border rounded-lg focus:border-violet-500 focus:ring-2 focus:ring-violet-500/50 transition-all outline-none`}
                              placeholder="0.00"
                           />
                           <span
                              className={`absolute right-4 top-1/2 transform -translate-y-1/2 text-xs ${
                                 isDark ? 'text-gray-400 bg-slate-950/80' : 'text-fuchsia-600 bg-fuchsia-100'
                              } px-2 py-1 rounded font-semibold`}>
                              MAX
                           </span>
                        </div>
                     </div>
                  </div>
               </div>
               {errors.rateRange && (
                  <p className={`text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>{errors.rateRange}</p>
               )}
            </div>

            {/* Number of Days */}
            <div className="space-y-2">
               <label
                  className={`block text-sm font-semibold ${
                     isDark ? 'text-gray-300' : 'text-slate-700'
                  } mb-2 flex items-center gap-2`}>
                  <Calendar className={`w-4 h-4 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
                  Monitoring Period <span className="text-red-500">*</span>
               </label>
               <input
                  ref={noOfDaysRef}
                  type="number"
                  name="noOfDays"
                  value={formData.noOfDays}
                  onChange={handleChange}
                  onWheel={(e) => e.target.blur()}
                  min="1"
                  className={`w-full px-4 py-3 ${
                     isDark
                        ? 'bg-slate-800/50 border-violet-500/30 text-white placeholder-gray-500'
                        : 'bg-violet-50 border-violet-300 text-slate-900 placeholder-slate-400'
                  } border rounded-xl focus:border-violet-500 focus:ring-2 focus:ring-violet-500/50 transition-all outline-none`}
                  placeholder="Number of days"
               />
               {errors.noOfDays && (
                  <p className={`text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>{errors.noOfDays}</p>
               )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
               <label
                  className={`block text-sm font-semibold ${
                     isDark ? 'text-gray-300' : 'text-slate-700'
                  } mb-2 flex items-center gap-2`}>
                  <FileText className={`w-4 h-4 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
                  Additional Notes
               </label>
               <textarea
                  ref={notesRef}
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  className={`w-full px-4 py-3 ${
                     isDark
                        ? 'bg-slate-800/50 border-violet-500/30 text-white placeholder-gray-500'
                        : 'bg-violet-50 border-violet-300 text-slate-900 placeholder-slate-400'
                  } border rounded-xl focus:border-violet-500 focus:ring-2 focus:ring-violet-500/50 transition-all outline-none resize-none`}
                  placeholder="Optional: Add conditions or special requirements..."
               />
            </div>

            {/* Submit Button */}
            <div className="px-8 py-6">
               <button type="submit" ref={submitButtonRef} disabled={submitted} className="relative w-full group">
                  <div
                     className={`absolute inset-0 bg-gradient-to-r ${
                        isDark ? 'from-violet-600 to-fuchsia-600' : 'from-violet-500 to-fuchsia-500'
                     } rounded-xl blur-lg group-hover:blur-xl transition-all ${isDark ? 'opacity-60' : 'opacity-40'}`}
                  />
                  <div
                     className={`relative bg-gradient-to-r ${
                        isDark ? 'from-violet-600 to-fuchsia-600' : 'from-violet-500 to-fuchsia-500'
                     } text-white font-bold py-4 rounded-xl shadow-lg transform group-hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3`}>
                     {submitted ? (
                        <>
                           <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                              <circle
                                 className="opacity-25"
                                 cx="12"
                                 cy="12"
                                 r="10"
                                 stroke="currentColor"
                                 strokeWidth="4"
                                 fill="none"
                              />
                              <path
                                 className="opacity-75"
                                 fill="currentColor"
                                 d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                           </svg>
                           <span>{editingAlert ? 'Updating Alert...' : 'Activating Alert...'}</span>
                        </>
                     ) : (
                        <>
                           <Bell className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                           <span>{editingAlert ? 'Update Rate Alert' : 'Activate Rate Alert'}</span>
                           <Sparkles className="w-5 h-5 group-hover:scale-110 transition-transform" />
                        </>
                     )}
                  </div>
               </button>
            </div>
         </form>
      </div>
   );

   return (
      <div
         className="fixed inset-0 z-50 flex items-center justify-center animate-in fade-in duration-200"
         onClick={(e) => {
            // Only close when form is not open
            if (!showForm) {
               onClose();
            }
         }}>
         {/* Backdrop */}
         <div className={`absolute inset-0 ${isDark ? 'bg-black/70' : 'bg-black/40'} backdrop-blur-xs`} />

         {/* Modal Content */}
         <div className="relative w-[1300px] animate-in zoom-in-95 duration-300" onClick={(e) => e.stopPropagation()}>
            {/* Modal Card */}
            <div
               className={`relative ${
                  isDark ? 'bg-gradient-to-br from-slate-900 to-slate-950' : 'bg-white'
               } rounded-3xl shadow-2xl ${
                  isDark ? 'border border-violet-500/20' : 'border border-violet-200'
               } overflow-hidden max-h-[90vh] overflow-y-auto`}>
               {/* Decorative Elements */}
               <div
                  className={`absolute top-0 right-0 w-64 h-64 ${
                     isDark ? 'bg-violet-600/20' : 'bg-violet-100/60'
                  } rounded-full blur-3xl`}
               />
               <div
                  className={`absolute bottom-0 left-0 w-64 h-64 ${
                     isDark ? 'bg-fuchsia-600/20' : 'bg-fuchsia-100/60'
                  } rounded-full blur-3xl`}
               />

               {/* Header */}
               <div
                  className={`relative px-8 pt-6 ${
                     isDark ? 'border-b border-violet-500/20' : 'border-b border-violet-200'
                  }`}>
                  <div className="flex items-center justify-between mb-4">
                     {showForm ? (
                        <div className="flex items-center gap-4">
                           <div className="relative">
                              <div
                                 className={`absolute inset-0 bg-gradient-to-r ${
                                    isDark ? 'from-violet-600 to-fuchsia-600' : 'from-violet-500 to-fuchsia-500'
                                 } rounded-2xl blur-xl ${isDark ? 'opacity-60' : 'opacity-40'}`}
                              />
                              <div
                                 className={`relative w-14 h-14 bg-gradient-to-br ${
                                    isDark ? 'from-violet-600 to-fuchsia-600' : 'from-violet-500 to-fuchsia-500'
                                 } rounded-2xl flex items-center justify-center`}>
                                 <Bell className="w-7 h-7 text-white" />
                              </div>
                           </div>
                           <div>
                              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                 {editingAlert ? 'Edit Rate Alert' : 'Rate Alert Notification'}
                              </h2>
                              <p className={`text-md ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                                 {editingAlert
                                    ? 'Update your rate monitoring settings'
                                    : 'Set up intelligent rate monitoring'}
                              </p>
                           </div>
                        </div>
                     ) : (
                        <div>
                           <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              Rate Alerts
                           </h2>
                           <p className={`text-md ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                              Manage your rate monitoring notifications
                           </p>
                        </div>
                     )}

                     {showForm ? (
                        <button
                           type="button"
                           ref={backButtonRef}
                           onClick={handleBackToList}
                           className={`flex items-center mr-10 gap-2 px-6 py-2 rounded-lg font-medium transition-all ${
                              isDark
                                 ? 'bg-slate-800 hover:bg-slate-700 text-gray-300'
                                 : 'bg-violet-100 hover:bg-violet-200 text-violet-600'
                           }`}>
                           Back to List
                        </button>
                     ) : (
                        <div className="flex justify-end mr-10">
                           <button
                              onClick={handleAddNew}
                              className={`flex items-center gap-2 px-6 py-2 rounded-xl font-semibold transition-all ${
                                 isDark
                                    ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white'
                                    : 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white'
                              } shadow-lg hover:shadow-xl transform hover:scale-105`}>
                              <Plus className="w-5 h-5" />
                              Add New Alert
                           </button>
                        </div>
                     )}
                     <div className="absolute top-0 right-0 m-2 mr-3">
                        <button
                           onClick={onClose}
                           className={`w-10 h-10 flex items-center justify-center rounded-xl ${
                              isDark
                                 ? 'bg-slate-800/50 hover:bg-slate-800 text-gray-400'
                                 : 'bg-violet-50 hover:bg-violet-100 text-violet-600'
                           } hover:text-white transition-all hover:rotate-90 duration-300`}>
                           <X className="w-5 h-5" />
                        </button>
                     </div>
                  </div>

                  {/* Action Buttons */}
               </div>

               {/* Content */}
               <div className="relative px-8 pt-4">{showForm ? renderFormView() : renderTableView()}</div>
            </div>
         </div>
      </div>
   );
};

export default NotifyMe;
